import React, { useState, useEffect, useContext } from "react";
//import { renderCount } from '../../../../appHelpers/appLoggers';
import styled from "styled-components";
import SettingIcon from "@rsuite/icons/legacy/Setting";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import { useAuth0 } from "@auth0/auth0-react";
import ImageIcon from "@rsuite/icons/Image";
import { CustomerUserListFC } from "./CustomerUserListFC";
import { CustomerSiteListFC } from "./CustomerSiteListFC";
import { CustomerSettingsFC } from "./CustomerSettingsFC";
import { CustomerSystemsListFC } from "./CustomerSystemsListFC";
import { CustomerAcumaticaFC } from "./CustomerAcumaticaFC";
import { CustomerSiteGroupsFC } from "./CustomerSiteGroupsFC";
import { InputContext } from "../../_context/InputContext";
import { SegmentedPicker, Button, GlassCard, Loader } from "../../../../ui";
import { colors } from "../../../../ui/theme/colors";
import DOMPurify from "dompurify";
import ArrowLeftLineIcon from "@rsuite/icons/ArrowLeftLine";

export const _CustomerFC = ({ client, userRoles, setPage }) => {
  // renderCount(`AdminFC`);
  const { getAccessTokenSilently } = useAuth0();
  const [customer, setCustomer] = useState();
  const [customerData, setCustomerData] = useState();
  const [clientLogo, setClientLogo] = useState();
  const [roles, setRoles] = useState();
  const [active, setActive] = useState("Sites");
  const [loadingCustomerData, setLoadingCustomerData] = useState([]);

  const { adminCustomerToggle, setAdminCustomerToggle } =
    useContext(InputContext);

  // Navigation options for SegmentedPicker
  const getNavigationOptions = () => {
    const baseOptions = [
      { eventKey: "Sites", label: "Sites" },
      { eventKey: "Site Groups", label: "Site Groups" },
      { eventKey: "Systems", label: "Systems" },
      { eventKey: "Users", label: "Users" },
    ];

    if (roles?.includes("superuser")) {
      baseOptions.push({ eventKey: "Acumatica", label: "Acumatica" });
    }

    baseOptions.push({ eventKey: "Settings", label: "Settings" });
    return baseOptions;
  };

  const getCustomerData = async () => {
    setLoadingCustomerData(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      let customerRes;

      if (adminCustomerToggle) {
        customerRes = await axiosInstance({
          url: `/customer/data/${adminCustomerToggle}`,
          data: ``,
          headers: { authorization: `Bearer ${token}` },
        });
      } else {
        customerRes = await axiosInstance({
          url: `/customer/data/${client}`,
          data: ``,
          headers: { authorization: `Bearer ${token}` },
        });
      }

      setCustomerData(customerRes?.data[0][0]);
      const logoSrc = customerRes?.data[0][0]?.logo_src;
      const fullLogoUrl = logoSrc?.startsWith("/uploads/")
        ? `${process.env.REACT_APP_BASE_EXPRESS_URL}${logoSrc}`
        : logoSrc;
      setClientLogo(<ImgSrcLogo src={DOMPurify.sanitize(fullLogoUrl)} />);
    } catch (error) {
      console.error(error);
    }
    setLoadingCustomerData(false);
  };

  const checkAdminCustomerToggle = () => {
    if (adminCustomerToggle) {
      getCustomerData();
      setCustomer(adminCustomerToggle);
      setRoles(userRoles);
    } else if (client) {
      getCustomerData();
      setCustomer(client);
      setRoles(userRoles);
    } else {
      setPage("Customers");
    }
  };

  useEffect(() => {
    checkAdminCustomerToggle();
  }, [adminCustomerToggle]);

  return loadingCustomerData ? (
    <_CustomerSC>
      <Loader
        size="md"
        content="Loading Customer Data..."
        subtext="Please wait while we fetch customer information"
        centered={true}
      />
    </_CustomerSC>
  ) : (
    <_CustomerSC>
      <BackButtonContainer>
        <Button
          variant="secondary"
          size="medium"
          onClick={() => {
            setAdminCustomerToggle(null);
            setPage("Customers");
          }}
        >
          ← Back
        </Button>
      </BackButtonContainer>

      <ContentWrapper>
        <CustomerProfileCard>
          <CustomerPane>
            <CustomerLogo>
              {customerData?.logo_src ? (
                clientLogo
              ) : (
                <ModernIconPlaceholder>
                  <ImageIcon />
                </ModernIconPlaceholder>
              )}
            </CustomerLogo>
            <CustomerName>
              {customerData?.name ? customerData.name : "No Customer Name"}
            </CustomerName>
            <CustomerSubText>Customer ID: {customerData?.id}</CustomerSubText>
          </CustomerPane>
        </CustomerProfileCard>

        <NavigationContainer>
          <SegmentedPicker
            options={getNavigationOptions()}
            activeKey={active}
            onSelect={setActive}
            variant="glass"
            size="medium"
          />
        </NavigationContainer>
      </ContentWrapper>

      <ContentPane>
        {active === "Users" ? (
          <CustomerUserListFC customer={customer} customerData={customerData} />
        ) : active === "Settings" ? (
          <CustomerSettingsFC customer={customerData} />
        ) : active === "Sites" ? (
          <CustomerSiteListFC customer={customer} />
        ) : active === "Site Groups" ? (
          <CustomerSiteGroupsFC customer={customer} />
        ) : active === "Systems" ? (
          <CustomerSystemsListFC customer={customer} />
        ) : active === "Acumatica" ? (
          <CustomerAcumaticaFC customer={customer} />
        ) : (
          <></>
        )}
      </ContentPane>
    </_CustomerSC>
  );
};

const _CustomerSC = styled.div`
  width: 100%;
  min-height: 100vh;
  padding: 8px 20px 40px 20px;
  color: ${({ theme, darkMode }) =>
    darkMode ? theme.dark_accent : theme.light_accent};

  @media screen and (max-width: 768px) {
    padding: 8px 16px 24px 16px;
  }
`;

const BackButtonContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding: 8px 0;
  position: sticky;
  top: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  z-index: 100;
  border-radius: 8px;

  @media screen and (max-width: 768px) {
    margin-bottom: 12px;
    padding: 6px 0;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-width: 1200px;
  margin: 0 auto;

  @media screen and (max-width: 768px) {
    gap: 14px;
  }
`;

const CustomerProfileCard = styled(GlassCard)`
  padding: 32px;
  text-align: center;

  @media screen and (max-width: 768px) {
    padding: 24px;
  }
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`;

const CustomerPane = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
`;

const CustomerName = styled.h2`
  font-size: 1.8em;
  font-weight: 600;
  margin: 0;
  color: ${colors.primary.main};
  text-align: center;

  @media screen and (max-width: 768px) {
    font-size: 1.5em;
  }
`;

const CustomerLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  height: 80px;

  @media screen and (max-width: 768px) {
    min-width: 70px;
    height: 70px;
  }
`;

const ModernIconPlaceholder = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  font-size: 32px;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    ${colors.primary.main} 0%,
    ${colors.primary.dark} 100%
  );
  box-shadow: 0 8px 24px rgba(44, 90, 160, 0.3);
  border: 3px solid rgba(255, 255, 255, 0.2);

  @media screen and (max-width: 768px) {
    width: 70px;
    height: 70px;
    font-size: 28px;
  }
`;

const NavigationSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: inherit;
  font-size: 1.5rem;
  font-weight: 600;
  color: ${({ theme, darkMode }) =>
    darkMode ? theme.dark_accent : theme.light_accent};

  .rs-icon {
    font-size: 1.5rem;
  }

  @media screen and (max-width: 768px) {
    font-size: 1.25rem;

    .rs-icon {
      font-size: 1.25rem;
    }
  }
`;

const NavigationWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  overflow-x: auto;
  padding: 0 8px;

  @media screen and (max-width: 768px) {
    justify-content: flex-start;
  }
`;

const ContentPane = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 16px auto 0 auto;

  @media screen and (max-width: 768px) {
    margin: 12px auto 0 auto;
  }
`;

const CustomerSubText = styled.div`
  font-size: 0.95em;
  color: ${colors.neutral[600]};
  font-weight: 500;
  text-align: center;
  padding: 8px 16px;
  background: rgba(44, 90, 160, 0.1);
  border-radius: 12px;
  border: 1px solid rgba(44, 90, 160, 0.2);

  @media screen and (max-width: 768px) {
    font-size: 0.9em;
  }
`;

const ImgSrcLogo = styled.img`
  height: 80px;
  width: 80px;
  object-fit: contain;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 24px rgba(44, 90, 160, 0.2);

  @media screen and (max-width: 768px) {
    height: 70px;
    width: 70px;
  }
`;
